import { Home, Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

type Props = {
  onHomeClick: () => void;
  isVisible: boolean; // true only after intro
};

const SlideNav = ({ onHomeClick, isVisible }: Props) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const links = [
    { to: "/about", label: "About" },
    { to: "/experience", label: "Experience" },
    { to: "/certifications", label: "Certifications" },
    { to: "/contact", label: "Contact" },
  ];

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  if (!isVisible) return null;

  const chromeStyle = {
    borderColor: "var(--surface-border)",
    background: "color-mix(in srgb, black 34%, transparent)",
  };

  return (
    <div
      className="absolute left-3 right-3 top-3 z-50 sm:left-auto sm:right-6 sm:top-6"
      style={{ top: "max(1rem, env(safe-area-inset-top))" }}
      ref={menuRef}
    >
      <div className="flex items-center justify-between gap-2 sm:ml-auto sm:max-w-max sm:justify-end sm:gap-3">
        <button
          onClick={onHomeClick}
          className="flex h-10 items-center gap-2 rounded-full border px-3 text-xs font-semibold text-[#0d1a19] shadow-[0_18px_55px_rgba(0,0,0,0.32)] transition hover:brightness-105 sm:h-12 sm:px-4 sm:text-sm"
          style={{
            borderColor: "color-mix(in srgb, var(--accent) 36%, transparent)",
            background:
              "linear-gradient(135deg, color-mix(in srgb, var(--accent) 95%, white), color-mix(in srgb, var(--accent-strong) 88%, transparent))",
          }}
          aria-label="Go to intro"
        >
          <Home size={18} />
          <span>Intro</span>
        </button>

        <div
          className="hidden items-center gap-2 rounded-full border p-2 backdrop-blur-2xl shadow-[0_22px_70px_rgba(0,0,0,0.28)] md:flex"
          style={chromeStyle}
        >
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="rounded-full px-4 py-2 text-sm font-medium text-[color:var(--text-muted)] transition hover:bg-white/10 hover:text-[color:var(--text-strong)]"
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="relative md:hidden">
          <button
            onClick={() => setOpen((prev) => !prev)}
            className="flex h-10 w-10 items-center justify-center rounded-full border text-[color:var(--text)] shadow-[0_18px_55px_rgba(0,0,0,0.28)] backdrop-blur-2xl transition hover:bg-black/50"
            style={chromeStyle}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>

          <div
            className={`absolute right-0 mt-2 w-[min(16rem,calc(100vw-1.5rem))] origin-top-right overflow-hidden rounded-[1.2rem] border p-2 shadow-[0_28px_80px_rgba(0,0,0,0.4)] backdrop-blur-2xl transition-all duration-300 ${
              open
                ? "pointer-events-auto scale-100 opacity-100"
                : "pointer-events-none scale-95 opacity-0"
            }`}
            style={chromeStyle}
          >
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="block rounded-2xl px-4 py-3 text-sm font-medium text-[color:var(--text-muted)] transition hover:bg-white/8 hover:text-[color:var(--text-strong)]"
                onClick={() => setOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlideNav;
